import {unitOfTime} from 'moment';
import { CalendarType } from './types';
import { Moment } from 'moment-timezone';
import { moment, now, __, __t } from '../../../app/support/helpers';
import CalendarEvent from '../../../app/models/CalendarEvent';
import { CalendarEventsByDay } from './../../../app/support/types';
import { range } from '../../../app/support/utils';


class Calendar {

    /**
     * @type {Calendar}
     */
    private static _instance: Calendar;

    /**
     * @type {Moment}
     */
    start: Moment;

    /**
     * @type {Moment}
     */
    end: Moment;

    /**
     * @type {CalendarEvent[]}
     */
    events: CalendarEvent[];

    /**
     * @var {CalendarType}
     */
    type: CalendarType;

    /**
     * Maximum showable items when the calendar is shrinked
     *
     * @var {number}
     */
    maximumShowable: number;

    /**
     * Flag to indicate if there are calendar events hidden from the sight
     *
     * @var {boolean}
     */
    hiddenEventsExists: boolean;

    /**
     * Constructor
     *
     * @param {CalendarEvent[]} events
     */
    constructor (events?: CalendarEvent[]) {

        if (Calendar._instance) {
            return Calendar._instance;
        }

        Calendar._instance = this;

        this.events = events || [];

        this.maximumShowable = 5;

        this.hiddenEventsExists = false;
    }

    /**
     * Set calendar events
     *
     * @param {CalendarEvent[]} events
     * @returns {Calendar}
     */
    setEvents(events: CalendarEvent[]): Calendar {
        this.events = events;
        return this;
    }

    /**
     * Set calendar type
     *
     * @param {CalendarType} type
     * @returns {Calendar}
     */
    setType(type: CalendarType): Calendar {
        this.type = type;
        return this;
    }

    /**
     * Set start date
     *
     * @param {Moment} start
     * @returns {Calendar}
     */
    setStart(start: Moment): Calendar {
        this.start = start;
        return this;
    }

    /**
     * Set end date
     *
     * @param {Moment} end
     * @returns {Calendar}
     */
    setEnd(end: Moment): Calendar {
        this.end = end;
        return this;
    }

    /**
     * Attach HTML to DOM
     *
     * @returns {void}
     */
    paint(): void {
        // Not used, just as a demo
        document.querySelector('.calendar').innerHTML = this.render();
    }

    /**
     * Render calendar HTML
     *
     * @returns {string}
     */
    render(): string {
        return `
            <div class="table_time_detail">
                ${ this.renderHeader() }
                <div class="liste_times font-tm schedulesContainer">
                    ${
                        this.closestAvailabilityExist()
                            ? this.renderClosestAvailability()
                            : (
                                this.isEmtpy()
                                    ? this.renderNoEvents()
                                    : (this.isSingleDay() ? this.renderColumn() : this.renderColumns())
                            )
                    }
                </div>
                <a id="showMoreAppointments" href="#" class="show_all_times font-tm ${ !this.hiddenEventsExists ? 'disabled-link' : '' }" rel="nofollow">
                    ${__t('Afficher plus d’horaires')}
                </a>
            </div>
        `;
    }

    /**
     * Render header
     *
     * @returns {string}
     */
    renderHeader(): string {
        return `
            <div class="navigationControl">
                <div class="right_header swipeDoctorCalendar">
                    <a id="navigateToLeft" href="#" rel="nofollow" class="dic_nav_left navigateToLeft" ${this.hideLeftNavController() ? 'style="display: none;"' : ''}><i class="" aria-hidden="true">
                        <span class="icon_left"></span></i>
                    </a>
                    <a id="navigateToRight" href="#" rel="nofollow" aria-label="Navigate to right" class="dic_nav_right navigateToRight">
                        <i class="" aria-hidden="true">
                            <span class="icon_right"></span>
                        </i>
                    </a>
                    <div class="bar-loader" style="display: none;"></div>
                    <ul class="ul_date font-tm">
                        ${ this.renderDays() }
                    </ul>
                </div>
            </div>
        `;
    }

    /**
     * Render calendar days
     *
     * @returns {string}
     */
    renderDays(): string {
        return this.period().map((date: Moment, index: number) => {
            return this.renderDay(date, index);
        }).join('');
    }

    /**
     * Render calendar day
     *
     * @param {Moment} date
     * @param {number} index
     * @returns {string}
     */
    renderDay(date: Moment, index: number): string {
        const isFirst = index === 0;
        return `
            <li ${isFirst ? 'class="current_day"' : '' }>
                <span class="date_day">${date.format('ddd')}</span>
                <span class="date_value">${date.format('DD/MM')}</span>
            </li>
        `;
    }

    /**
     * Render calendar columns (the case of "week" calendar)
     *
     * @returns {string}
     */
    renderColumns(): string {
        return this.period().map((date: Moment, index: number) => {
            const isFirst      = index === 0;
            const events       = this.groupedByDate(date);
            const dayHasEvents = events.length > 0;
            return `
                <div class="times ${ isFirst ? 'current_time firstDayContainer' : '' }">
                    <div class="ul_date_value2 ${ isFirst ? 'firstDayWrapper' : '' }">
                        <ul class="date_top ${ isFirst ? 'firstDayUl' : '' }">
                            ${
                                dayHasEvents
                                    ? this.renderEvents(events)
                                    : this.renderFakeEvents()
                            }
                        </u>
                    </div>
                </div>
            `;
        }).join('');
    }

    /**
     * Render calendar column (the case of "single_day" calendar)
     *
     * @returns {string}
     */
     renderColumn(): string {
        return this.period().map((date: Moment, index: number) => {
            const isFirst = index === 0;
            return `
                <div class="times ${ isFirst ? 'current_time firstDayContainer' : '' }">
                    <div class="ul_date_value2 ${ isFirst ? 'firstDayWrapper' : '' }">
                        <ul class="date_top ${ isFirst ? 'firstDayUl' : '' }">
                            ${
                                this.isEmtpy()
                                    ? this.renderFakeEvents()
                                    : this.renderEvents(this.events)
                            }
                        </u>
                    </div>
                </div>
            `;
        }).join('');
    }

    /**
     * Render Calendar Events
     *
     * @param {CalendarEvent[]} calendarEvents
     * @returns {string}
     */
    renderEvents(calendarEvents: CalendarEvent[]): string {
        // previously known as "total_fake_appointments"
        const totalFakeEvents    = this.maximumShowableEvents() - calendarEvents.length;
        // previously known as "showable_fake_appointments"
        const showableFakeEvents = this.maximumShowable - calendarEvents.length;

        const bookableEvents     = calendarEvents.filter((calendarEvent: CalendarEvent) => calendarEvent.isBookable());

        const _events            = bookableEvents.length ? bookableEvents : calendarEvents;

        const hasOnlyClosestEvent = _events.every((calendarEvent) => calendarEvent.isClosest());

        if (hasOnlyClosestEvent) {
            return this.renderEvent(_events[0]);
        }

        const events = _events.map((calendarEvent: CalendarEvent, index: number) => {
            return this.renderEvent(calendarEvent, index);
        }).join('');

        // event + complementary fake appointments to equalizer the calendar view
        return events + this.renderFakeEvents(totalFakeEvents, showableFakeEvents);
    }

    /**
     * Render calendar event
     *
     * @param {CalendarEvent} calendarEvent
     * @returns {string}
     */
    renderEvent(calendarEvent: CalendarEvent, index?: number): string {
        if (calendarEvent.isClosest()) {
            return `
                <li>
                    <div class="availability-message-container">
                        <p class="availability-message">
                            <a href="#" class="nextAvailability" data-next-availability='${JSON.stringify(calendarEvent)}' rel="nofollow">
                                ${__('next_appointment_on')} ${calendarEvent.startDate('DD/MM/YYYY')}
                            </a>
                            <span class="icon_right"></span>
                        </p>
                    </div>
                </li>
            `;
        }

        // previously known as "show_more_appointments"
        const isHidden = index > this.maximumShowable;
        this.hiddenEventsExists = isHidden;
        return `
            <li ${ isHidden ? 'class="extraAppointment" style="display: none;"' : '' }>
                <a
                    href="${ calendarEvent.isBookable() ? calendarEvent.bookingUrl() : 'javascript:void(0)' }"
                    class="time ${ calendarEvent.isBookable() ? 'bookAppointment' : 'disabled-rdv' } ${calendarEvent.isRemote() ? 'video-call-calendar' : ''}"
                    data-event="${ JSON.stringify(calendarEvent) }"
                    rel="nofollow"
                >
                    ${ calendarEvent.startTime() }
                </a>
            </li>
        `;
    }

    /**
     * Render fake events
     *
     * @param {number} total
     * @param {number} numberOfShowableEvents
     * @returns {string}
     */
    renderFakeEvents(total?: number, numberOfShowableEvents?: number): string {
        total = total >= 0 /* because total could be 0 or < 0 */ ? total : this.maximumShowableEvents();
        numberOfShowableEvents = numberOfShowableEvents || this.maximumShowable;
        return range(1, total).map((_, index) => {
            return this.renderFakeEvent(index > numberOfShowableEvents);
        }).join('');
    }

    /**
     * Render fake event
     *
     * @param {boolean} isHidden
     * @returns {string}
     */
    renderFakeEvent(isHidden: boolean): string {
        return `
            <li ${ isHidden ? 'class="extraAppointment" style="display: none;"' : ''} >
                <a onclick="return:false;" class="time disabled-rdv" rel="nofollow">
                    --
                </a>
            </li>
        `;
    }

    /**
     * Render no availability
     *
     * @returns {string}
     */
    renderNoEvents(): string {
        return `
            <div class="availability-message-container">
                <p class="availability-message">
                    ${__t('Aucune disponibilité en ligne')}.
                </p>
            </div>
        `;
    }

    /**
     * Render closest availibility
     *
     * @returns {string}
     */
    renderClosestAvailability(): string {
        const calendarEvent = this.firstClosestAvailability();
        return `
            <div class="availability-message-container">
                <p class="availability-message">
                    <a href="#" class="nextAvailability" data-next-availability='${JSON.stringify(calendarEvent)}' rel="nofollow">
                        ${__('next_appointment_on')} ${calendarEvent.startDate('DD/MM/YYYY')}
                    </a>
                    <span class="icon_right"></span>
                </p>
            </div>
        `;
    }

    /**
     * Maximum showable events (previously known as "max_appointments_in_day")
     *
     * if calendar is empty or max appointments found in a day of the week is less than
     * maximumShowable (previously known as "max_showable"), we return maximumShowable,
     * so we have a consistent view even if the calendar has no appointments or
     * has appointments less than maximumShowable
     *
     * @returns {number}
     */
    maximumShowableEvents(): number {
        if (this.isEmtpy()) {
            return this.maximumShowable;
        }

        return Math.max(this.maximumShowable, this.maxNumberEventsPerDay());
    }

    /**
     * Get max number of events per day
     *
     * @returns {number}
     */
    maxNumberEventsPerDay(): number {
        const events = this.groupedByDate();
        return Math.max(...this.period().map((day) => {
            const eventsInDay = events[day.format('YYYY-MM-DD')] || [];
            return eventsInDay.length
        }));
    }

    /**
     * Group events by day
     *
     * @returns {CalendarEventsByDay}
     */
    groupedByDate(): CalendarEventsByDay;
    groupedByDate(date: Moment|string): CalendarEvent[];
    groupedByDate(date?: Moment|string): CalendarEventsByDay|CalendarEvent[] {
        const groupedEvents = CalendarEvent.groupByDay(this.events);
        if (!date) return groupedEvents;
        date = typeof date === 'string' ? moment(date) : date;
        const day = date.format('YYYY-MM-DD');
        return groupedEvents[day] || [];
    }

    /**
     * Check if a closest availibility exists
     *
     * @returns {boolean}
     */
    closestAvailabilityExist(): boolean {
        return !!this.firstClosestAvailability();
    }

    /**
     * Get first closest availability
     *
     * @returns {CalendarEvent}
     */
    firstClosestAvailability(): CalendarEvent {
        return this.events.find((calendarEvent) => calendarEvent.isClosest());
    }

    /**
     * Get period between start and end dates
     *
     * @param {unitOfTime.Diff} interval
     * @returns {Moment[]}
     */
    period(interval?: unitOfTime.Diff): Moment[] {
        interval = interval || 'days';
        // @ts-ignore
        const range = moment().range(this.start, this.end);
        return Array.from(range.by(interval)) as Moment[];
    }

    /**
     * Check if calendar has no events
     *
     * @returns {boolean}
     */
    isEmtpy(): boolean {
        return !this.events.length;
    }

    /**
     * Check if calendar type is "single day"
     *
     * @returns {boolean}
     */
    isSingleDay(): boolean {
        return this.type === 'single_day';
    }

    /**
     * Check if calendar type is "week"
     *
     * @returns {boolean}
     */
    isWeek(): boolean {
        return this.type === 'week'
    }

    /**
     * Check when it's appropriate to hide or show calendar's left nav controller
     *
     * @returns {boolean}
     */
    hideLeftNavController(): boolean {
        return this.start.isSame(now().format('YYYY-MM-DD'), 'day') || this.start.isBefore();
    }
}

const calendar = new Calendar();

export default calendar;
